import { PLAYER } from './game/config.js';

// TEMP: side-only character render (no front-facing pose)
const SIDE_ONLY = true;
// Toggle for variable-height ("Mario-style") jumps. Disabled per request.
const VARIABLE_JUMP = false;
// When true, holding jump will automatically trigger a double-jump at the apex (if available).
// Enable to make double jump fluid while holding jump.
const AUTO_APEX_AIR_JUMP = true;

export class Character {
  constructor(x, y) {
    this.x = x; this.y = y;
    this.vx = 0; this.vy = 0;
    this.w = PLAYER.WIDTH; this.h = PLAYER.HEIGHT;
    this.onGround = false;
    this.facing = 1;
    this.animTime = 0; this.state = 'idle';
    this.airJumpUsed = false;
    this.justDidAirJump = false;
    this.prevVy = 0;
    // Jump helpers
    this.coyoteTimer = 0;
    this.jumpBufferTimer = 0;
    this._prevJumpHeld = false;
  }

  // Static sprite assets
  static get sprites() {
    if (!this._sprites) {
      const front = new Image(); front.src = 'img_assets/character/char.png';
      const side = new Image();  side.src  = 'img_assets/character/char_profile.png';
      const sideFlash = new Image(); sideFlash.src = 'img_assets/character/char_profile_flash_red.png';
      this._sprites = { front, side, sideFlash };
    }
    return this._sprites;
  }

  static getOpaqueBounds(img) {
    if (!img || !img.naturalWidth || !img.naturalHeight) return null;
    if (!this._bounds) this._bounds = new Map();
    if (this._bounds.has(img)) return this._bounds.get(img);
    const w = img.naturalWidth, h = img.naturalHeight;
    const off = document.createElement('canvas');
    off.width = w; off.height = h;
    const c = off.getContext('2d');
    c.clearRect(0, 0, w, h);
    c.drawImage(img, 0, 0);
    const data = c.getImageData(0, 0, w, h).data;
    let minX = w, minY = h, maxX = -1, maxY = -1;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const a = data[(y * w + x) * 4 + 3];
        if (a > 8) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    const bounds = (maxX < 0)
      ? { left: 0, right: 0, top: 0, bottom: 0, width: 0, height: 0 }
      : { left: minX, right: w - 1 - maxX, top: minY, bottom: h - 1 - maxY, width: (maxX - minX + 1), height: (maxY - minY + 1) };
    this._bounds.set(img, bounds);
    return bounds;
  }

  update(dt, input, world) {
    const speedMul = Math.max(0.1, world.speedMul || 1);
    const WALK_MAX = 160 * speedMul;
    const RUN_MAX  = 260 * speedMul;
    const MAX_SPEED = (input.runHeld ? RUN_MAX : WALK_MAX);
    const ACCEL = (input.runHeld ? 1100 : 900) * speedMul;
    const JUMP_V = 420;
    const GRAV = 1500 * (world.gravityMul ?? 1);
    const FRICTION = 2200 * speedMul;
    const COYOTE_TIME = 0.10;   // seconds after walking off ledge
    const JUMP_BUFFER = 0.12;   // seconds to buffer jump input
    const JUMP_CUT_EXTRA = 2.2; // extra gravity multiplier while ascending if jump not held (unused when VARIABLE_JUMP=false)
    const SECOND_JUMP_MULT = Math.sqrt(1.5); // 50% higher height => sqrt(1.5) velocity

    let ax = 0;
    if (input.leftHeld)  ax -= ACCEL;
    if (input.rightHeld) ax += ACCEL;
    if (ax === 0) {
      // Apply friction when no input, whether on ground or in air
      // (acts as air resistance when airborne, prevents wall jitter)
      const frictionAmount = this.onGround ? FRICTION : FRICTION * 0.5;
      const sign = Math.sign(this.vx);
      const mag = Math.max(0, Math.abs(this.vx) - frictionAmount * dt);
      this.vx = mag * sign;
    } else {
      this.vx = Math.max(-MAX_SPEED, Math.min(MAX_SPEED, this.vx + ax * dt));
    }
    if (this.vx !== 0) this.facing = Math.sign(this.vx);

    // Coyote time bookkeeping
    if (this.onGround) this.coyoteTimer = COYOTE_TIME; else this.coyoteTimer = Math.max(0, this.coyoteTimer - dt);
    // Buffer jump on press
    if (input.consumeJump()) this.jumpBufferTimer = JUMP_BUFFER;
    else this.jumpBufferTimer = Math.max(0, this.jumpBufferTimer - dt);

    this.justDidAirJump = false;
    // Use buffered jump if allowed
    if (this.jumpBufferTimer > 0) {
      if (this.onGround || this.coyoteTimer > 0) {
        this.vy = -JUMP_V; this.onGround = false; this.airJumpUsed = false; this.jumpBufferTimer = 0; this.coyoteTimer = 0;
      } else if (world.doubleJump && !this.airJumpUsed) {
        this.vy = -JUMP_V * SECOND_JUMP_MULT; this.airJumpUsed = true; this.justDidAirJump = true; this.jumpBufferTimer = 0;
      }
    }

    // Apply gravity only when airborne to prevent on-ground jitter from constant tiny hops
    if (!this.onGround) {
      this.vy += GRAV * dt;
    } else {
      this.vy = 0;
    }
    // Variable jump height disabled unless VARIABLE_JUMP is true
    if (VARIABLE_JUMP) {
      if (this.vy < 0 && !input.jumpHeld) {
        this.vy += GRAV * (JUMP_CUT_EXTRA - 1) * dt;
      }
    }

    // Auto-trigger air jump at apex if holding jump (disabled unless AUTO_APEX_AIR_JUMP)
    if (AUTO_APEX_AIR_JUMP) {
      if (!this.onGround && world.doubleJump && !this.airJumpUsed && input.jumpHeld) {
        // Be forgiving around apex: trigger when we cross upward->downward
        // or when vertical speed is near zero within a small epsilon.
        const APEX_EPS = 18; // px/s tolerance
        if ((this.prevVy < 0 && this.vy >= 0) || (this.prevVy < 0 && this.vy > -APEX_EPS)) {
          this.vy = -JUMP_V * SECOND_JUMP_MULT; this.airJumpUsed = true; this.justDidAirJump = true;
        }
      }
    }

    this.x += this.vx * dt;
    this.y += this.vy * dt;

    if (!this.onGround) this.state = 'jump';
    else if (Math.abs(this.vx) > 10) this.state = 'walk';
    else this.state = 'idle';

    this.animTime += dt;
    this.prevVy = this.vy;
    this._prevJumpHeld = !!input.jumpHeld;
  }

  draw(ctx) {
    const { x, y, w, h } = this;

    ctx.save();
    ctx.translate(Math.round(x), Math.round(y));
    const sprites = Character.sprites;
    const movingHoriz = Math.abs(this.vx) > 10;
    const useSide = SIDE_ONLY || (this.state === 'walk' || (this.state === 'jump' && movingHoriz));
    const img = useSide ? sprites.side : sprites.front;
    const ready = img && img.complete && img.naturalWidth;
    if (ready) {
      // Preserve aspect ratio, draw larger than collision box, and align the
      // opaque bottom of the sprite to the ground (avoids visual hovering due
      // to transparent padding).
      const aspect = img.naturalWidth / img.naturalHeight;
      const scale = 1.8; // visual size multiplier baseline
      const dh = Math.round(this.h * scale);
      const dw = Math.round(dh * aspect);
      const drawX = Math.round((this.w - dw) / 2);
      const bounds = Character.getOpaqueBounds(img);
      const opaqueBottomSrc = bounds ? (bounds.top + bounds.height) : img.naturalHeight;
      const scalePxY = dh / img.naturalHeight; // pixels per source pixel vertically
      const drawY = Math.round(this.h - opaqueBottomSrc * scalePxY);

      ctx.save();
      if (this.facing === -1) {
        // Flip horizontally when last movement was left
        ctx.translate(drawX + dw, drawY);
        ctx.scale(-1, 1);
        ctx.drawImage(img, 0, 0, dw, dh);
      } else {
        ctx.drawImage(img, drawX, drawY, dw, dh);
      }
      ctx.restore();
    } else {
      // fallback placeholder
      ctx.fillStyle = '#ffd34d';
      roundRect(ctx, 0, 0, w, h, 6);
      ctx.fill();
    }

    // Preserve the simple "two dots" walk bob animation on top
    const bob = this.footBob();
    Character.drawFeetPrimitive(ctx, w, h, bob);

    ctx.restore();
  }

  footBob() {
    return (this.state === 'walk') ? Math.sin(this.animTime * 14) * 2 : 0;
  }

  static drawFeetPrimitive(ctx, w, h, bob) {
    ctx.fillStyle = '#1f2440';
    ctx.fillRect(3, h - 6 + bob, 7, 6);
    ctx.fillRect(w - 10, h - 6 - bob, 7, 6);
  }

  drawFeet(ctx) {
    ctx.save();
    ctx.translate(Math.round(this.x), Math.round(this.y));
    Character.drawFeetPrimitive(ctx, this.w, this.h, this.footBob());
    ctx.restore();
  }
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y,     x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x,     y + h, r);
  ctx.arcTo(x,     y + h, x,     y,     r);
  ctx.arcTo(x,     y,     x + w, y,     r);
  ctx.closePath();
}
